import { STALE_TIME_RESOURCES } from "@impero-com/common/utils/query_client";
import { InfiniteData, keepPreviousData } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { useGetControlResultsInfinite } from "api/generated/control-results/control-results";
import {
    ArchivedStatus,
    ExistingControlResultPaginatedListDto,
    GetControlResultsParams,
} from "api/generated/model";

import { deduplicateArrayByKeyCallback } from "../../dashboard/utils/filterUtils";

interface ControlResultsParams {
    controlProgramIds?: number[];
    controlIds?: string[];
    dueDateStart?: string;
    dueDateEnd?: string;
    periodDateStart?: string;
    periodDateEnd?: string;
    limit?: number;
    archivedStatus?: ArchivedStatus;
    classificationId?: string | null;
    enabled?: boolean;
}

export function useExistingControlResultsQuery(params: ControlResultsParams) {
    const filterParams: GetControlResultsParams = {
        ...(params.controlProgramIds && {
            control_program_ids: params.controlProgramIds,
        }),
        ...(params.controlIds && {
            control_ids: params.controlIds,
        }),
        ...(params.dueDateStart && {
            due_date_start: params.dueDateStart,
        }),
        ...(params.dueDateEnd && {
            due_date_end: params.dueDateEnd,
        }),
        ...(params.periodDateStart && {
            period_date_start: params.periodDateStart,
        }),
        ...(params.periodDateEnd && {
            period_date_end: params.periodDateEnd,
        }),
        ...(params.limit && {
            limit: params.limit,
        }),
        ...(params.archivedStatus !== undefined && {
            archived_status: params.archivedStatus,
        }),
        ...(params.classificationId && {
            classification_id: params.classificationId,
        }),
    };

    return useGetControlResultsInfinite(filterParams, {
        query: {
            enabled: params.enabled ?? true,
            initialPageParam: "",
            // Keep existing results visible during date-range refetches.
            // Replace them seamlessly when the new data arrives.
            placeholderData: keepPreviousData,
            getNextPageParam: (lastPage) => lastPage.data.pagination.next,
            select,
            staleTime: STALE_TIME_RESOURCES,
            // Let the route ErrorBoundary render the failure, like the other list pages.
            throwOnError: true,
        },
    });
}

const select = (
    data: InfiniteData<AxiosResponse<ExistingControlResultPaginatedListDto>>
): ExistingControlResultPaginatedListDto => {
    return {
        content: data.pages.flatMap((page) => page.data.content),
        linkedResources: {
            controlPrograms: data.pages
                .flatMap((page) => page.data.linkedResources.controlPrograms)
                .filter(deduplicateArrayByKeyCallback((cp) => cp.resource.id)),
            controls: data.pages.flatMap(
                (page) => page.data.linkedResources.controls
            ),
            users: data.pages.flatMap(
                (page) => page.data.linkedResources.users
            ),
            controlResultUserPools: data.pages.flatMap(
                (page) => page.data.linkedResources.controlResultUserPools
            ),
            entities: data.pages
                .flatMap((page) => page.data.linkedResources.entities)
                .filter(deduplicateArrayByKeyCallback((e) => e.id)),
            classification: data.pages[0].data.linkedResources.classification,
        },
        pagination: {
            prev: null,
            next: null,
        },
    };
};
